import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { HelpId } from 'src/app/core/routing/help-id';
import { AuthenticationHttpService } from 'src/providers/authentication-http.service';
import { SessionManagementService } from 'src/services/session-management.service';
import { HelpService } from 'src/services/help.service';
import { UserContextService } from 'src/services/user-context.service';

/**
 * Handles post-navigation side effects: session reset and help-context update.
 * Google Analytics tracking is handled by RouterEventsService.
 */
@Injectable({
  providedIn: 'root'
})
export class AngularNavigationService {

  constructor(
    private router: Router,
    private authenticationHttpService: AuthenticationHttpService,
    private sessionManagementService: SessionManagementService,
    private helpService: HelpService,
    private userContextService: UserContextService
  ) {}

  initialize(): void {
    // Subscribe to successful navigation events (equivalent to onTransitionSuccess)
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.handleNavigationSuccess(event);
    });
  }

  private handleNavigationSuccess(event: NavigationEnd): void {
    try {
      this.handleSessionManagement(event);
      this.handleHelpContextUpdate();
    } catch (error) {
      console.error('AngularNavigationService: Error handling navigation success', error);
    }
  }

  private handleSessionManagement(event: NavigationEnd): void {
    if (this.authenticationHttpService.isAuthenticated() && !this.isUnauthenticatedRoute(event.url)) {
      this.sessionManagementService.resetSession();
    }
  }

  /**
   * Handle help system context updates.
   * Reads helpId and unauthenticated flag from the activated route's declared config data rather
   * than the merged snapshot data, so named-outlet layout stubs cannot shadow a parent's values.
   */
  private handleHelpContextUpdate(): void {
    try {
      const chain = this.getPrimaryChain(this.router.routerState.root.snapshot);
      const leaf = chain[chain.length - 1];

      const state = {
        // permissionKey covers authenticated routes; helpRouteName carries the state name for
        // unauthenticated public routes (help.service switches on it). Only one is ever set.
        name: this.resolveDeclared<string>(chain, d => (d?.['permissionKey'] ?? d?.['helpRouteName']) as string | undefined) ?? '',
        unauthenticated: !!(this.resolveDeclared<unknown>(chain, d => d?.['unauthenticated'])),
      };
      const helpId = this.resolveDeclared<HelpId>(chain, d => d?.['helpId']);

      const helpPath = this.helpService.determineHelpPath(state, leaf?.queryParams ?? {}, this.userContextService, helpId);
      this.helpService.setHelpUrlPath(helpPath);
    } catch (error) {
      console.error('AngularNavigationService: Error updating help context', error);
    }
  }

  /**
   * Determine if a route is unauthenticated based on URL
   * This replaces checking toState.unauthenticated from UI-Router
   */
  private isUnauthenticatedRoute(url: string): boolean {
    const unauthenticatedPaths = [
      '/login',
      '/forgotPassword',
      '/resetPassword',
      '/expiredPassword',
      '/agencyLogin',
      '/authenticate',
      '/signInHelpWorkflow',
      '/ssoExpired',
      '/ssoLogin',
      '/account-recovery-workflow',
      '/accountRecoveryWorkflow', // stale camelCase bookmarks/links redirect to the kebab route; keep both unauthenticated so session isn't reset pre-redirect
      '/verifyBackupEmailAddress',
      '/activateEmailAddressChange',
      '/activatePendingInsuranceEmail',
      '/retainInsuranceAccount',
      '/inviteSignup',
      '/agencyDecline',
      '/error'
    ];

    return unauthenticatedPaths.some(path => url.startsWith(path));
  }

  /**
   * Root→leaf chain following ONLY primary-outlet children.
   *
   * `ActivatedRouteSnapshot.firstChild` is `children[0]` regardless of outlet name, so it can
   * descend into named-outlet layout stubs (content/header/sideRail/footer on multi-outlet
   * routes). Angular's `emptyOnly` inheritance shallow-spreads `data`, so a stub's `data` REPLACES
   * the parent's, which could lose the parent's `permissionKey`/`helpId`. Walking only
   * primary-outlet children avoids that entirely. Matches the `routeConfig.data` pattern in
   * `EnhancedAuthGuard.hasPermissionedDescendant()`.
   */
  private getPrimaryChain(root: ActivatedRouteSnapshot): ActivatedRouteSnapshot[] {
    const chain: ActivatedRouteSnapshot[] = [root];
    let current = root.children.find(c => c.outlet === PRIMARY_OUTLET);
    while (current) {
      chain.push(current);
      current = current.children.find(c => c.outlet === PRIMARY_OUTLET);
    }
    return chain;
  }

  /**
   * Read a field from the DEEPEST route in the chain that DECLARES it.
   *
   * Reads `routeConfig.data` (declared config) rather than the snapshot's merged `data`.
   * This is independent of `paramsInheritanceStrategy` and immune to placeholder children
   * shadowing an ancestor's value via data inheritance.
   */
  private resolveDeclared<T>(
    chain: ActivatedRouteSnapshot[],
    pick: (data: Record<string, unknown> | undefined) => T | undefined
  ): T | undefined {
    for (let i = chain.length - 1; i >= 0; i--) {
      const value = pick(chain[i].routeConfig?.data as Record<string, unknown> | undefined);
      if (value !== undefined) return value;
    }
    return undefined;
  }
}